"use client";

import clsx, { type ClassValue } from "clsx";
import Link from "next/link";
import { cn } from "@/lib/utils";
import { isExternalUrl } from "@/utils/isExternalUrl";

export type RedesignButtonProps = {
  children: React.ReactNode;
  htmlType?: "button" | "submit" | "reset";
  href?: string;
  target?: "_blank" | "_self";
  rel?: string;
  disabled?: boolean;
  iconAfter?: React.ReactNode;
  className?: ClassValue;
  onClick?: React.MouseEventHandler<HTMLElement>;
};

const baseClassName = clsx(
  "inline-flex items-center justify-center gap-2",
  "rounded-md px-5 py-2.5",
  "font-monument text-sm font-medium leading-none",
  "bg-neutral-900 text-white",
  "cursor-pointer transition-colors",
  "hover:bg-neutral-800 active:bg-neutral-800",
  "disabled:cursor-not-allowed disabled:bg-neutral-900/50 disabled:text-white/70",
);

export function RedesignButton({
  children,
  htmlType = "button",
  href,
  target,
  rel,
  disabled,
  iconAfter,
  className,
  onClick,
}: Readonly<RedesignButtonProps>) {
  const external = href ? isExternalUrl(href) : false;
  const linkTarget = target ?? (external ? "_blank" : "_self");
  const linkRel = rel ?? (external ? "noopener noreferrer" : undefined);

  const content = (
    <>
      {children}
      {iconAfter}
    </>
  );

  if (href && !disabled) {
    return (
      <Link
        href={href}
        target={linkTarget}
        rel={linkRel}
        onClick={onClick}
        className={cn(baseClassName, className)}
      >
        {content}
      </Link>
    );
  }

  return (
    <button
      type={htmlType}
      disabled={disabled}
      onClick={onClick}
      className={cn(baseClassName, className)}
    >
      {content}
    </button>
  );
}
